import React, { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { VoidChecksContext, FormValues } from './index.context'
import { useForm, useWatch } from 'react-hook-form'
import { defaultValues } from './index.constant'
import { useSelector } from 'react-redux'
import { getUserProfile } from 'src/app/store/auth'
import { USER_ROLE } from 'src/app/shared/constants/app.constants'
import { useClient } from 'src/app/shared/hooks/useClient'
import { IOption } from 'src/app/shared/models/common.model'
import { getAccountAssigned } from 'src/app/shared/services/ClientSetupService'
import { getTransactions } from 'src/app/shared/services/TransactionService'
import { STATUS_CODES } from 'src/app/shared/constants/common.constants'
import { IGetTransactions } from 'src/app/shared/models/Transaction.model'
import { IOuterTableRef, TableColumnDefs } from 'src/app/shared/components/common/new-table/table.model'
import { formatNumberWithCommas } from 'src/app/shared/utils'
import { useTranslation } from 'react-i18next'
import { defaultPageSize, defaultPagination } from 'src/app/shared/components/common/new-table/table.constants'
import dayjs from 'dayjs'

const VoidChecksProvider: React.FC<PropsWithChildren> = (props) => {
  const { children } = props
  const userProfile = useSelector(getUserProfile)
  const { clients, loadingClients, handleGetClients, handleGetClientInfoByUser } = useClient()
  const { t } = useTranslation()

  const [loading, setLoading] = useState<boolean>(false)
  const [loadingAccounts, setLoadingAccounts] = useState<boolean>(false)
  const [accountOptions, setAccountOptions] = useState<IOption[]>([])
  const [transactionData, setTransactionData] = useState<IGetTransactions.Response>({
    items: [],
    total: 0,
  })
  const [hasSearched, setHasSearched] = useState<boolean>(false)
  const [currentSearchParams, setCurrentSearchParams] = useState<FormValues | null>(null)
  const [currentPage, setCurrentPage] = useState<number>(defaultPagination)
  const [isFirstTimeSearch, setIsFirstTimeSearch] = useState<boolean>(true)
  const [pageSize, setPageSize] = useState<number>(defaultPageSize)
  const [lastTablePayload, setLastTablePayload] = useState<any>({})
  const outerTableRef = useRef<IOuterTableRef>(null)

  const form = useForm<FormValues>({
    defaultValues,
    mode: 'all',
  })

  const clientId = useWatch({
    control: form.control,
    name: 'clientId',
  })

  const isBankUser = useMemo(() => { return userProfile?.role === USER_ROLE.BANK }, [userProfile?.role])

  // Load clients on mount based on role
  useEffect(() => {
    if (userProfile?.role === USER_ROLE.CLIENT) {
      handleGetClientInfoByUser()
    } else {
      handleGetClients()
    }
  }, [userProfile?.role, handleGetClients, handleGetClientInfoByUser])

  // Set client for client users after loading
  useEffect(() => {
    if (userProfile?.role === USER_ROLE.CLIENT && userProfile?.client) {
      form.setValue('clientId', userProfile?.client?.id)
    }
  }, [userProfile?.client, userProfile?.role, form])
  
  // Load accounts when client changes
  useEffect(() => {
    if (clientId) {
      form.setValue('accountIds', null as any)
      setAccountOptions([])
      setLoadingAccounts(true)
      setHasSearched(false)
      
      getAccountAssigned({
        clientIds: [clientId],
        isBankUser,
        userId: userProfile?.userId || '',
      })
        .then((res: any) => {
          if (res?.data) {
            setAccountOptions(
              res.data.map((item: any) => ({
                label: item.accountName,
                value: item.id,
              }))
            )
          }
        })
        .catch(() => {
          setAccountOptions([])
        })
        .finally(() => {
          setLoadingAccounts(false)
        })
    } else {
      setAccountOptions([])
      form.setValue('accountIds', [])
      setLoadingAccounts(false)
      setHasSearched(false)
    }
  }, [clientId, form, isBankUser, userProfile?.userId])

  const clientOptions: IOption[] = useMemo(() => {
    return (
      clients?.map((client: any) => ({
        label: client.clientName,
        value: client.id,
      })) ?? []
    )
  }, [clients])

  const disabled = useMemo(() => {
    return userProfile?.role === USER_ROLE.CLIENT
  }, [userProfile?.role])

  const transformSearchParams = useCallback(
    (searchParams: FormValues) => {
      const payload: Partial<IGetTransactions.Payload> = {
        clientIds: searchParams.clientId ? [searchParams.clientId] : [],
        accountIds: searchParams.accountIds === null 
          ? accountOptions.map((acc) => acc.value)
          : (searchParams.accountIds ?? []),
        transactionType: 2, // Checks only
        dateType: 0, // Issued Date (0 = Issued, 1 = Paid, 2 = Input, 3 = Exception)
      }

      if (searchParams.checkNumber) {
        const checkNum = parseFloat(searchParams.checkNumber)
        payload.checkNumberFrom = checkNum
        payload.checkNumberTo = checkNum
      }

      if (searchParams.amount) {
        const amt = parseFloat(searchParams.amount)
        payload.amountFrom = amt
        payload.amountTo = amt
      }

      if (searchParams.date && searchParams.date.length > 0) {
        const dateFormat = 'YYYY-MM-DD'
        payload.dateFrom = searchParams.date[0] ? dayjs(searchParams.date[0]).format(dateFormat) : undefined
        payload.dateTo = searchParams.date[1] ? dayjs(searchParams.date[1]).format(dateFormat) : undefined
      }

      return payload
    },
    [accountOptions]
  )

  // Internal search executor - handles both initial search and pagination
  const executeSearch = useCallback(
    async (searchParams: FormValues, additionalPayload?: any) => {
      setLoading(true)
      try {
        const searchPayload = {
          ...transformSearchParams(searchParams),
          pageIndex: additionalPayload?.pageIndex ?? currentPage,
          pageSize: additionalPayload?.pageSize ?? pageSize,
          ...additionalPayload,
        }
        
        const res = await getTransactions(searchPayload as IGetTransactions.Payload)
        if (res.statusCode === STATUS_CODES.SUCCESS && res.data) {
          setTransactionData(res.data)
          if (additionalPayload?.pageIndex) setCurrentPage(additionalPayload.pageIndex)
          if (additionalPayload?.pageSize) setPageSize(additionalPayload.pageSize)
          if (additionalPayload) setLastTablePayload(additionalPayload)
        }
      } catch (error) {
        console.error('Error fetching transactions:', error)
        setTransactionData({ items: [], total: 0 })
      } finally {
        setLoading(false)
      }
    },
    [transformSearchParams, currentPage, pageSize]
  )

  const onSubmit = useCallback(
    async (values: FormValues) => {
      setCurrentSearchParams(values)
      setCurrentPage(defaultPagination)
      if (isFirstTimeSearch) {
        setHasSearched(true)
        setIsFirstTimeSearch(false)
      }
      await executeSearch(values, {
        pageIndex: defaultPagination,
        pageSize: pageSize,
        sortColumnsDictionary: { issuedDate: 'asc' },
      })
      setHasSearched(true)
    },
    [loadingAccounts, executeSearch, pageSize, hasSearched]
  )

  const handleSearch = useCallback(
    async (payload: any) => {
      if (!currentSearchParams) return
      await executeSearch(currentSearchParams, payload)
    },
    [currentSearchParams, executeSearch]
  )

  const reloadTable = useCallback(
    async () => {
      if (!currentSearchParams) return
      await executeSearch(currentSearchParams, lastTablePayload)
    },
    [currentSearchParams, executeSearch, lastTablePayload]
  )

  const handleDownload = useCallback(
    async (payload: any) => {
      if (!currentSearchParams) return
      
      const searchPayload = {
        ...transformSearchParams(currentSearchParams),
        pageIndex: currentPage,
        pageSize: pageSize,
        ...payload,
        export: true,
      }
      return await getTransactions(searchPayload)
    },
    [currentSearchParams, transformSearchParams, currentPage, pageSize]
  )

  const onReset = useCallback(() => {
    form.reset(defaultValues)
    setTransactionData({ items: [], total: 0 })
    setHasSearched(false)
    setCurrentSearchParams(null)
    setCurrentPage(defaultPagination)
    setPageSize(defaultPageSize)
    // Restore client for client users
    if (userProfile?.role === USER_ROLE.CLIENT && clients.length > 0) {
      form.setValue('clientId', clients[0].id)
    }
  }, [form, userProfile, clients])

  return (
    <VoidChecksContext.Provider
      value={{
        form,
        clientOptions,
        accountOptions,
        loading: loadingClients || loading || loadingAccounts,
        searchLoading: loading || loadingAccounts,
        disabled,
        onSubmit,
        onReset,
        transactionData,
        hasSearched,
        outerTableRef,
        handleSearch,
        reloadTable,
        handleDownload,
        currentPage,
        pageSize,
      }}
    >
      {children}
    </VoidChecksContext.Provider>
  )
}

export default VoidChecksProvider
